import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Skeleton from '@mui/material/Skeleton';

import { createMarkerContent, loadScript, map_settings } from "../../../map";
import { fetchJobMapDetails } from "../service";

const Map = ({ job_id }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);

    const [loading, setLoading] = useState(true);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [jobMapDetails, setJobMapDetails] = useState(null);

    /* =============================
       Helper: Build marker content
    ============================== */
    const buildMarkerContent = useCallback(({ iconColor, iconSize, iconName }) => {
        const content = document.createElement("div");
        content.innerHTML = `
            <div style="color: ${iconColor};">
                <i class="material-icons" style="font-size: ${iconSize}">${iconName}</i>
            </div>`;
        return content;
    }, []);

    /* =============================
       Helper: Clear all markers
    ============================== */
    const clearMarkers = useCallback(() => {
        markersRef.current.forEach(marker => {
            if (marker && marker.map) {
                marker.map = null;
            }
        });
        markersRef.current = [];
    }, []);

    /* =============================
       Fetch job map details
    ============================== */
    useEffect(() => {
        if (!job_id) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetchJobMapDetails(job_id);

                if (response?.status === "Success") {
                    setJobMapDetails(response.data);
                }
            } catch (err) {
                console.error("Job map details error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [job_id]);

    /* =============================
       Load Google Maps script
    ============================== */
    useEffect(() => {
        loadScript(
            map_settings.map_script_url,
            () => setMapLoaded(true),
            () => console.error("Failed to load Google Maps script")
        );
    }, []);

    /* =============================
       Validate coordinates
    ============================== */
    const coordinates = useMemo(() => {
        if (!jobMapDetails?.job) return null;

        const lat = Number(jobMapDetails.job.job_latitude);
        const lng = Number(jobMapDetails.job.job_longitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

        return { lat, lng };
    }, [jobMapDetails?.job]);

    /* =============================
       Initialize map (once)
    ============================== */
    useEffect(() => {
        if (!mapLoaded || !coordinates || mapInstance.current || !window.google?.maps) {
            return;
        }

        mapInstance.current = new window.google.maps.Map(mapRef.current, {
            center: coordinates,
            zoom: 12,
            mapId: "JOB_MAP",
        });
    }, [mapLoaded, coordinates]);

    /* =============================
       Render markers for status history
    ============================== */
    useEffect(() => {
        if (!mapInstance.current || !jobMapDetails?.status_history || !coordinates) return;

        // Clear existing markers
        clearMarkers();

        const bounds = new window.google.maps.LatLngBounds();
        let hasValidMarkers = false;

        // Add main job marker
        const mainMarker = new window.google.maps.marker.AdvancedMarkerElement({
            map: mapInstance.current,
            position: coordinates,
            content: createMarkerContent(map_settings.ftm_map_icon),
            title: jobMapDetails.job.job_address,
        });
        markersRef.current.push(mainMarker);
        bounds.extend(coordinates);
        hasValidMarkers = true;

        // Add status history markers
        jobMapDetails.status_history.forEach(item => {
            const lat = item.job_status_change_log_latitude;
            const lng = item.job_status_change_log_longitude;

            if (lat === null || lng === null) return;

            const position = new window.google.maps.LatLng(lat, lng);

            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                map: mapInstance.current,
                position: position,
                content: buildMarkerContent({
                    iconColor: item?.job_status?.job_status_color,
                    iconSize: '35px',
                    iconName: item?.job_status?.job_status_icon
                }),
                title: item?.job_status?.job_status_name || jobMapDetails.job.job_address,
            });

            markersRef.current.push(marker);
            bounds.extend(position);
            hasValidMarkers = true;
        });

        // Fit bounds only if we have markers
        if (hasValidMarkers) {
            mapInstance.current.fitBounds(bounds);
            
            // Set zoom after fitBounds completes
            window.google.maps.event.addListenerOnce(mapInstance.current, 'bounds_changed', () => {
                const currentZoom = mapInstance.current.getZoom();
                if (currentZoom > 12) {
                    mapInstance.current.setZoom(12);
                }
            });
        }
    }, [jobMapDetails, coordinates, buildMarkerContent, clearMarkers]);

    /* =============================
       Cleanup on unmount
    ============================== */
    useEffect(() => {
        return () => {
            clearMarkers();
        };
    }, [clearMarkers]);

    return (
        <div className="tab-content shadow-dark border-radius-lg" id="v-pills-tabContent">
            {loading ? <Skeleton variant="rounded" width={"100%"} height={400} /> : (
                <div 
                    className="tab-pane fade show position-relative active height-400 border-radius-lg" 
                    id="cam1" role="tabpanel" 
                    aria-labelledby="cam1" 
                    loading="lazy"
                    ref={mapRef} 
                    style={{ width: "100%", height: 675 }}
                />
            )}
        </div>
    );
};

export default Map;